import { BdsGrid } from 'blip-ds/dist/blip-ds-react/components';
import { Grid } from 'components';
import { AgentStatus } from 'components/AgentStatus/AgentStatus';
import { Skeleton } from 'components/Skeleton/Skeleton';
import { TicketsInAttendance } from 'components/TicketsInAttendance/TicketsInAttendance';
import { TicketsInQueue } from 'components/TicketsInQueue';
import { TicketStatus } from 'components/TicketStatus';
import { useAppContext } from 'contexts/AppContext';
import { useLoadingContext } from 'contexts/LoadingContext';
import { Installation } from 'interfaces';
import { useEffect, useRef, useState } from 'react';
import { threatMetrics } from 'services/Helpers/threatMetrics';
import { AttendanceMetrics, TicketsResponse } from 'typings';
import { TableDataModel } from 'typings/components/TableDataModel';
import { MetricsLoadingProps } from 'typings/Loading';
import { TicketsInfo } from 'typings/services/TicketsInfo';
import { secondsToDHM } from 'utils';
import { fetchAttendanceMetrics, fetchTicketsInfo } from './services/services';
import './UpperMetrics.scss';

type UpperMetricsProps = {
    bots: Installation[];
    isBotLoading: boolean;
    ticketsInAttendance: TableDataModel<TicketsResponse['tickets']>;
    ticketsInQueue: TableDataModel<TicketsResponse['tickets']>;
    isLoadingTicketsInAttendance: boolean;
    isLoadingTicketsInQueue: boolean;
};

export const UpperMetrics: React.FC<UpperMetricsProps> = ({
    bots,
    isBotLoading,
    ticketsInAttendance,
    ticketsInQueue,
    isLoadingTicketsInAttendance,
    isLoadingTicketsInQueue,
}) => {
    const {
        appServices: { reportService, packsService },
    } = useAppContext();
    const { isToUpdateAll, upperMetricsIsLoading, setUpperMetricsIsLoading } = useLoadingContext();

    // Create a ref to hold the current AbortController for metrics
    const metricsAbortControllerRef = useRef<AbortController | null>(null);
    const botsRef = useRef(bots);
    botsRef.current = bots;
    const botsKey = bots.map(b => b.installationId).join(',');

    const [attendanceMetrics, setAttendanceMetrics] = useState<AttendanceMetrics>();
    const [ticketsInfo, setTicketsInfo] = useState<TicketsInfo>();
    const [metricsLoading, setMetricsLoading] = useState<MetricsLoadingProps>({
        isTicketMetricsLoading: false,
        isAttendanceMetricsLoading: false,
    });

    useEffect(() => {
        const someMetricIsLoading = metricsLoading.isTicketMetricsLoading || metricsLoading.isAttendanceMetricsLoading;
        if (someMetricIsLoading) {
            setUpperMetricsIsLoading(true);
        } else {
            setUpperMetricsIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metricsLoading]);

    useEffect(() => {
        if (upperMetricsIsLoading) return;
        if (!ticketsInfo?.inAttendance) return;
        if (!ticketsInfo?.inAttendance) return;

        if (botsRef.current.length > 1) {
            setAttendanceMetrics(prev => {
                if (!prev) return prev;
                const sumStatus =
                    prev.attendants.online.amount +
                    prev.attendants.pause.amount +
                    prev.attendants.invisible.amount;
                const amount = sumStatus === 0 ? 0 : Math.ceil(ticketsInfo.inAttendance / sumStatus);
                return {
                    ...prev,
                    attendants: {
                        ...prev.attendants,
                        ticketsPerAttendant: { ...prev.attendants.ticketsPerAttendant, amount },
                    },
                };
            });
            setAttendanceMetrics(prev => {
                if (!prev) return prev;
                const sumStatus =
                    prev.attendants.online.amount +
                    prev.attendants.pause.amount +
                    prev.attendants.invisible.amount;
                const amount = sumStatus === 0 ? 0 : Math.ceil(ticketsInfo.inAttendance / sumStatus);
                return {
                    ...prev,
                    attendants: {
                        ...prev.attendants,
                        ticketsPerAttendant: { ...prev.attendants.ticketsPerAttendant, amount },
                    },
                };
            });
        }
    }, [upperMetricsIsLoading, botsKey, ticketsInfo?.inAttendance]);

    useEffect(() => {
        if (metricsAbortControllerRef.current) {
            metricsAbortControllerRef.current.abort();
        }

        metricsAbortControllerRef.current = new AbortController();

        setMetricsLoading({
            isTicketMetricsLoading: false,
            isAttendanceMetricsLoading: false,
        });

        return () => {
            if (metricsAbortControllerRef.current) {
                metricsAbortControllerRef.current.abort();
            }
        };
    }, [botsKey]);

    useEffect(() => {
        if (!reportService || !metricsAbortControllerRef.current) return;

        if (bots && botsRef.current.length > 0) {
            fetchAttendanceMetrics(
                setMetricsLoading,
                reportService,
                botsRef.current,
                attendanceMetrics,
                setAttendanceMetrics,
                metricsAbortControllerRef.current,
            );
            fetchTicketsInfo(setTicketsInfo, setMetricsLoading, botsRef.current, packsService, metricsAbortControllerRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportService, botsKey]);

    useEffect(() => {
        if (isToUpdateAll && metricsAbortControllerRef.current) {
            fetchAttendanceMetrics(
                setMetricsLoading,
                reportService,
                botsRef.current,
                attendanceMetrics,
                setAttendanceMetrics,
                metricsAbortControllerRef.current,
            );
            fetchTicketsInfo(setTicketsInfo, setMetricsLoading, botsRef.current, packsService, metricsAbortControllerRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isToUpdateAll]);

    return (
        <BdsGrid direction="column" gap="2">
            {isBotLoading ? (
                <Grid margin="t-2">
                    <Skeleton
                        direction="row"
                        skeletons={[
                            { shape: 'square', width: '30%', height: '172px' },
                            { shape: 'square', width: '70%', height: '172px' },
                        ]}
                    />
                </Grid>
            ) : (
                <BdsGrid className="metrics-row" direction="row" justifyContent="space-between" gap="2" margin="t-2">
                    <TicketsInQueue
                        ticketsCount={ticketsInQueue.data.length ?? 0}
                        maxQueueTime={secondsToDHM(ticketsInfo?.maxTimeInQueueInSeconds)}
                        loading={{
                            ...metricsLoading,
                            isTicketsLoading: isLoadingTicketsInQueue,
                            ticketsPercentageLoaded: ticketsInQueue.percentageLoaded,
                        }}
                    />
                    <TicketsInAttendance
                        ticketsCount={ticketsInAttendance.data.length ?? 0}
                        averageFirstResponseTime={threatMetrics(
                            attendanceMetrics?.tickets.inAttendance.timeMetrics.firstResponseTime.avg,
                        )}
                        maxFirstResponseTime={threatMetrics(
                            attendanceMetrics?.tickets.inAttendance.timeMetrics.firstResponseTime.max,
                        )}
                        averageResponseTime={threatMetrics(
                            attendanceMetrics?.tickets.inAttendance.timeMetrics.responseTime.avg,
                        )}
                        averageAttendanceTime={threatMetrics(
                            attendanceMetrics?.tickets.inAttendance.timeMetrics.attendanceTime.avg,
                        )}
                        showAverages={bots.length == 1}
                        loading={{
                            ...metricsLoading,
                            isTicketsLoading: isLoadingTicketsInAttendance,
                            ticketsPercentageLoaded: ticketsInAttendance.percentageLoaded,
                        }}
                    />
                </BdsGrid>
            )}

            {isBotLoading ? (
                <Skeleton
                    direction="row"
                    skeletons={[
                        { shape: 'square', width: '40%', height: '172px' },
                        { shape: 'square', width: '60%', height: '172px' },
                    ]}
                />
            ) : (
                <BdsGrid className="metrics-row" direction="row" justifyContent="space-between" gap="2">
                    <TicketStatus
                        totalToday={ticketsInfo?.totalToday ?? 0}
                        closedToday={ticketsInfo?.closedToday ?? 0}
                        closedByAttendant={attendanceMetrics?.tickets.closedByAttendant.amount ?? 0}
                        closedByClient={attendanceMetrics?.tickets.closedByClient.amount ?? 0}
                        missed={attendanceMetrics?.tickets.missed.amount ?? 0}
                        loading={metricsLoading}
                    />
                    <AgentStatus
                        averageTicketsPerAttendant={attendanceMetrics?.attendants.ticketsPerAttendant.amount ?? 0}
                        online={attendanceMetrics?.attendants.online.amount ?? 0}
                        pause={attendanceMetrics?.attendants.pause.amount ?? 0}
                        invisible={attendanceMetrics?.attendants.invisible.amount ?? 0}
                        loading={metricsLoading}
                    />
                </BdsGrid>
            )}
        </BdsGrid>
    );
};
